import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserPreferencesControllerService } from '@scip/api';
import { UserPreferenceDto } from '@scip/api';
import { map, retry } from 'rxjs/operators';

export enum UiSettingsIdentifier {
  ProductsTable = 'ProductsTable',
  SubmissionsTable = 'SubmissionsTable_v2',
  ScipUiSettings = 'ScipUiSettings'
}

@Injectable({
  providedIn: 'root'
})
export class UiUserSettingsService {
  // This is our service to safe UI-settings in the db. If the developer knows that a change
  // in a component which is saving/restoring settings via this way is incompatible with the
  // old structure which is saved in the db, then he should use/increment the "version"-parameter
  // He does not need to check it on his side, he just can use that indicator for it.
  constructor(private userPreferencesControllerService: UserPreferencesControllerService) {}

  getUiSettingsFor(type: UiSettingsIdentifier, version?: string): Observable<Record<string, any>> {
    return this.userPreferencesControllerService.get(type).pipe(
      retry(1),
      map((response: UserPreferenceDto) => response.value),
      map((value: string) => {
        if (value && value.length) {
          return JSON.parse(value); // We know the saved user settings are in real a JSON object, so convert it
        } else {
          return undefined; // The user has no saved settings as he never changed anything, so its undefined
        }
      }),
      map((parsedSettings: Record<string, any>) => {
        if (version && parsedSettings?.version !== version) {
          return undefined;
        } else if (parsedSettings) {
          delete parsedSettings.version;
          return parsedSettings;
        } else {
          return undefined;
        }
      })
    );
  }

  saveUiSettingsFor(type: UiSettingsIdentifier, settings: Record<string, any>, version?: string): Observable<boolean> {
    let newSettings = { ...settings };
    if (version) {
      newSettings = { ...settings, version };
    }
    const settingsToSave: UserPreferenceDto = {
      type: type,
      // eslint-disable-next-line @typescript-eslint/quotes
      value: JSON.stringify(newSettings).replace("'", '"')
    };
    return this.userPreferencesControllerService.update(settingsToSave).pipe(
      retry(1),
      map(() => true)
    );
  }
}
